import React, { useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { submitComment } from './consultationApi';
import TurnstileWidget, { TURNSTILE_SITE_KEY } from './TurnstileWidget';

// Off-screen rather than display:none, which some bots detect and skip.
const honeypotStyle = { position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' };

const MAX_NAME = 120;
const MAX_COMMENT = 10000;

/**
 * Posts a comment, or a reply when parentId is given; the CMS publishes it straight away
 * and emails the consultation's contacts. Replies use a compact layout with a Cancel button.
 */
export default function CommentForm({ postId, parentId, replyTo, onSubmitted, onCancel }) {
    const isReply = Boolean(parentId);
    const idSuffix = isReply ? `reply-${parentId}` : 'new';
    const [authorName, setAuthorName] = useState('');
    const [content, setContent] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaFailed, setCaptchaFailed] = useState(false);
    const [website, setWebsite] = useState('');
    const submitting = useRef(false);
    const captcha = useRef(null);

    async function submit(event) {
        event.preventDefault();
        if (submitting.current) return;
        setError('');
        setSuccess(false);

        const name = authorName.trim();
        const text = content.trim();
        if (!name || name.length > MAX_NAME) {
            setError(`Enter your name (up to ${MAX_NAME} characters).`);
            return;
        }
        if (!text || text.length > MAX_COMMENT) {
            setError(`Enter your ${isReply ? 'reply' : 'comment'} (up to ${MAX_COMMENT} characters).`);
            return;
        }
        if (TURNSTILE_SITE_KEY && !captchaToken) {
            setError(captchaFailed
                ? 'The security check could not load. Please reload the page and try again.'
                : 'Please complete the security check below, then submit again.');
            return;
        }

        submitting.current = true;
        setBusy(true);
        try {
            const result = await submitComment({
                postId, parentId, authorName: name, content: text, captchaToken, website,
            });
            if (result === 'ok') {
                setAuthorName('');
                setContent('');
                setSuccess(true);
                if (onSubmitted) onSubmitted();
            } else if (result === 'rate-limited') {
                setError('Too many comments in a short time. Please wait a minute and try again.');
            } else if (result === 'captcha') {
                setError('The security check failed. Please complete it again and resubmit.');
            } else if (result === 'unavailable') {
                setError('Comments are temporarily unavailable. Please try again later.');
            } else if (result === 'closed') {
                setError(isReply
                    ? 'This reply could not be posted. The comment may have been removed, or the consultation has closed.'
                    : 'This consultation is no longer accepting comments.');
            } else {
                setError('Your comment could not be sent. Please try again later.');
            }
        } catch {
            setError('Your comment could not be sent. Check your connection and try again.');
        } finally {
            submitting.current = false;
            setBusy(false);
            // Tokens are single-use, whatever the outcome; get a fresh one for the next attempt.
            if (captcha.current) captcha.current.reset();
        }
    }

    return (
        <form onSubmit={submit} aria-busy={busy} noValidate>
            <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: isReply ? 12 : 16 }}>
                <TextField
                    id={`consultation-name-${idSuffix}`}
                    label="Your name"
                    helperText={isReply ? undefined : 'Shown publicly with your comment.'}
                    variant="outlined"
                    size={isReply ? 'small' : 'medium'}
                    required
                    fullWidth
                    value={authorName}
                    onChange={(event) => { setAuthorName(event.target.value); setSuccess(false); }}
                    inputProps={{ maxLength: MAX_NAME, autoComplete: 'name' }}
                />
                <TextField
                    id={`consultation-comment-${idSuffix}`}
                    label={isReply ? 'Your reply' : 'Your comment'}
                    placeholder={isReply && replyTo ? `Reply to ${replyTo}…` : undefined}
                    helperText={`${content.length}/${MAX_COMMENT} characters`}
                    variant="outlined"
                    size={isReply ? 'small' : 'medium'}
                    required
                    fullWidth
                    multiline
                    minRows={isReply ? 3 : 5}
                    value={content}
                    onChange={(event) => { setContent(event.target.value); setSuccess(false); }}
                    inputProps={{ maxLength: MAX_COMMENT }}
                />
                <div style={honeypotStyle} aria-hidden="true">
                    <label htmlFor={`consultation-website-${idSuffix}`}>Leave this field empty</label>
                    <input
                        id={`consultation-website-${idSuffix}`}
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={website}
                        onChange={(event) => setWebsite(event.target.value)}
                    />
                </div>
                {TURNSTILE_SITE_KEY && (
                    <TurnstileWidget
                        ref={captcha}
                        onToken={(token) => { setCaptchaToken(token); if (token) setCaptchaFailed(false); }}
                        onError={() => setCaptchaFailed(true)}
                    />
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="submit" variant="contained" color="primary" size={isReply ? 'small' : 'medium'} disabled={busy}>
                        {busy ? 'Sending…' : isReply ? 'Post reply' : 'Submit comment'}
                    </Button>
                    {onCancel && (
                        <Button type="button" size="small" onClick={onCancel} disabled={busy}>
                            Cancel
                        </Button>
                    )}
                </div>
            </fieldset>
            {error && <p role="alert" style={{ color: '#a62121', marginTop: 12 }}>{error}</p>}
            {success && (
                <p role="status" style={{ color: '#1f7a55', marginTop: 12 }}>
                    Thank you. Your comment has been published.
                </p>
            )}
        </form>
    );
}
